import { useState, useEffect } from 'preact/hooks';
import { EmailSummaryConfig } from '@/types/email-summary';
import { api_fetch } from '@/api/client';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

interface EmailSummaryConfigProps {
  className?: string;
  orgId: string;
}

export function EmailSummaryConfigComponent({ className, orgId }: EmailSummaryConfigProps) {
  const [config, setConfig] = useState<EmailSummaryConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchConfig();
  }, [orgId]);

  async function fetchConfig() {
    setLoading(true);
    const result = await api_fetch<EmailSummaryConfig>(`/email-summary/config`, {
      params: { org_id: orgId }
    });
    
    if (result.ok) {
      setConfig(result.value.data);
      setError(null);
    } else {
      setError(result.error.message || 'Failed to load email configuration');
    }
    setLoading(false);
  }

  async function saveConfig() {
    if (!config) return;
    
    setSaving(true);
    setError(null);
    
    // Include the id when updating - the backend needs it to identify the record
    const result = await api_fetch<EmailSummaryConfig>('/email-summary/config', {
      method: 'POST',
      body: config,
    });
    
    if (result.ok) {
      setConfig(result.value.data);
      alert('Configuration saved successfully!');
    } else {
      setError(result.error.message || 'Failed to save configuration');
    }
    setSaving(false);
  }

  async function triggerEmail() {
    setSendingTest(true);
    setError(null);
    
    const result = await api_fetch(`/email-summary/trigger?org_id=${orgId}`, {
      method: 'POST',
      body: {},
    });
    
    if (result.ok) {
      alert('Email triggered successfully!');
    } else {
      setError(result.error.message || 'Failed to trigger email');
    }
    
    setSendingTest(false);
  }

  function updateConfig(updates: Partial<EmailSummaryConfig>) {
    if (!config) return;
    setConfig({ ...config, ...updates });
  }

  async function createDefaultConfig() {
    setSaving(true);
    setError(null);
    
    const now = new Date().toISOString();
    const result = await api_fetch<EmailSummaryConfig>('/email-summary/config', {
      method: 'POST',
      body: {
        id: crypto.randomUUID(),
        org_id: orgId,
        created_at: now,
        updated_at: now,
        frequency: 'biweekly',
        status: 'active',
        day_of_week: 5, // Friday 
        hour_of_day: 12, // 12 PM
        timezone: 'America/New_York',
        include_ai_insights: true,
        last_sent_at: null,
        next_send_at: null,
      },
    });
    
    if (result.ok) {
      setConfig(result.value.data);
    } else {
      setError(result.error.message || 'Failed to create email configuration');
    }
    setSaving(false);
  }

  if (loading) {
    return (
      <div class={`p-6 ${className || ''}`}>
        <div class="animate-pulse">
          <div class="h-8 bg-gray-200 rounded mb-4"></div>
          <div class="space-y-4">
            <div class="h-4 bg-gray-200 rounded"></div>
            <div class="h-4 bg-gray-200 rounded"></div>
            <div class="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div class={`p-6 ${className || ''}`}>
        <div class="max-w-2xl mx-auto">
          {error && (
            <div class="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p class="text-red-600">{error}</p>
            </div>
          )}
          
          <div class="text-center py-8">
            <p class="text-gray-600 mb-4">No email configuration found for this organization.</p>
            <Button
              variant="default"
              onClick={createDefaultConfig}
              disabled={saving}
              class="px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Default Configuration'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div class={`p-6 ${className || ''}`}>
      <div class="max-w-2xl mx-auto">
        {error && (
          <div class="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p class="text-red-600">{error}</p>
          </div>
        )}

        <div class="space-y-6">
          {/* Status */}
          <div>
            <Label class="block text-sm font-medium text-gray-700 mb-2">
              Email Status
            </Label>
            <Select
              value={config.status}
              onValueChange={(value) => updateConfig({ status: value as any })}
            >
              <SelectTrigger class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Frequency */}
          <div>
            <Label class="block text-sm font-medium text-gray-700 mb-2">
              Email Frequency
            </Label>
            <Select
              value={config.frequency}
              onValueChange={(value) => updateConfig({ frequency: value as any })}
            >
              <SelectTrigger class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500">
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="biweekly">Biweekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Day of Week */}
          <div>
            <Label class="block text-sm font-medium text-gray-700 mb-2">
              Day of Week
            </Label>
            <Select
              value={config.day_of_week.toString()}
              onValueChange={(value) => updateConfig({ day_of_week: parseInt(value) })}
            >
              <SelectTrigger class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500">
                <SelectValue placeholder="Select day" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Sunday</SelectItem>
                <SelectItem value="1">Monday</SelectItem>
                <SelectItem value="2">Tuesday</SelectItem>
                <SelectItem value="3">Wednesday</SelectItem>
                <SelectItem value="4">Thursday</SelectItem>
                <SelectItem value="5">Friday</SelectItem>
                <SelectItem value="6">Saturday</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Hour of Day */}
          <div>
            <Label class="block text-sm font-medium text-gray-700 mb-2">
              Hour of Day (24-hour format)
            </Label>
            <Select
              value={config.hour_of_day.toString()}
              onValueChange={(value) => updateConfig({ hour_of_day: parseInt(value) })}
            >
              <SelectTrigger class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500">
                <SelectValue placeholder="Select hour" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 24 }, (_, i) => (
                  <SelectItem key={i} value={i.toString()}>
                    {i.toString().padStart(2, '0')}:00
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Timezone */}
          <div>
            <Label class="block text-sm font-medium text-gray-700 mb-2">
              Timezone
            </Label>
            <Select
              value={config.timezone}
              onValueChange={(value) => updateConfig({ timezone: value })}
            >
              <SelectTrigger class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                <SelectItem value="UTC">UTC</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* AI Insights */}
          <div class="flex items-center space-x-2">
            <Checkbox
              checked={config.include_ai_insights}
              onCheckedChange={(checked) => updateConfig({ include_ai_insights: checked as boolean })}
            />
            <Label class="text-sm font-medium text-gray-700">
              Include AI-generated insights in emails
            </Label>
          </div>

          {/* Next Send Time */}
          {config.next_send_at && (
            <div>
              <Label class="block text-sm font-medium text-gray-700 mb-2">
                Next Scheduled Send
              </Label>
              <p class="text-sm text-gray-600">
                {formatInTimeZone(parseISO(config.next_send_at), config.timezone, 'EEEE, MMMM do, yyyy \'at\' h:mm a zzz')}
              </p>
            </div>
          )}

          {/* Last Send Time */}
          {config.last_sent_at && (
            <div>
              <Label class="block text-sm font-medium text-gray-700 mb-2">
                Last Sent
              </Label>
              <p class="text-sm text-gray-600">
                {formatInTimeZone(parseISO(config.last_sent_at), config.timezone, 'EEEE, MMMM do, yyyy \'at\' h:mm a zzz')}
              </p>
            </div>
          )}
        </div>

        <div class="mt-8 flex space-x-4">
          <Button
            onClick={saveConfig}
            disabled={saving}
            variant="default"
            class="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
          
          <Button
            onClick={triggerEmail}
            variant="outline"
            disabled={sendingTest}
            class="px-4 py-2 border-green-600 text-green-600 rounded-md hover:bg-green-50 disabled:opacity-50"
          >
            {sendingTest ? 'Sending...' : 'Send Test Email'}
          </Button>
        </div>
      </div>
    </div>
  );
}
